import React, { useContext, useState, useCallback, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Avatar,
  IconButton,
} from '@mui/material'
import { IntakeContext } from 'context/IntakeContext'
import { useModal } from 'components/modals'
import { apiClient } from 'utils/api/apiClient'

import {
  AccountBalance,
  Check,
  CreditCard,
  Home,
  InfoOutlined,
  Link,
  LocalHospital,
  MedicalServices,
  People,
  RequestQuote,
  Savings,
  VolunteerActivism,
  ChildCare,
} from '@mui/icons-material'
const iconMap = {
  checkingsavings: Savings,
  property: Home,
  creditcard: CreditCard,
  investment: AccountBalance,
  mortgage: Home,
  charity: VolunteerActivism,
  alotchecking: Savings,
  alotother: Savings,
  alotsavings: Savings,
  fdvv: LocalHospital,
  fdvd: LocalHospital,
  ltc: LocalHospital,
  visiondental: LocalHospital,
  extratax: RequestQuote,
  uniondues: People,
  fsa: MedicalServices,
  hsa: MedicalServices,
  vbp: MedicalServices,
  child: ChildCare,
}
const AccountIconSelector = ({ subType, ...props }) => {
  const IconComponent = iconMap[subType] || AccountBalance
  return <IconComponent {...props} />
}

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

function LiabilityReview({ demoMode = false }) {
  const [liabilities, setLiabilities] = useState([])
  const { intakeState } = useContext(IntakeContext)
  const { openModal } = useModal()

  const getAccounts = useCallback(async () => {
    const {
      data: { items },
    } = await apiClient.get('api/assets')
    const foundLiabilities =
      items?.filter(({ type }) => type === 'liability') || []
    setLiabilities(foundLiabilities)
  }, [])

  useEffect(() => {
    getAccounts()
  }, [getAccounts])

  const handleClick = (callback) => {
    if (demoMode) return undefined
    return callback
  }

  const totalLiabilities = (liabilities || [])
    .map((a) => a.amount || 0)
    .reduce((a, b) => a + b, 0)

  return (
    <Box p={2} width="100%" bgcolor="#fff">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="body1Medium">Total Liabilities</Typography>
        <Typography variant="body1Medium">
          {dollarFormat.format(totalLiabilities)}
        </Typography>
      </Box>

      {/* Liabilities Table */}
      <TableContainer sx={{ border: '1px solid #262627' }}>
        <Table>
          <TableBody>
            {liabilities.map((item, index) => {
              const name = item.name || 'Unnamed Liability'
              const amount = item.amount || 0
              const logo = item.img || item.externalInstitutionLogo

              const onClick = handleClick(() =>
                openModal('editAccount', {
                  accountType: 'Liability',
                  account: item,
                  getAccounts,
                })
              )

              return (
                <TableRow
                  key={item.id ?? index}
                  hover
                  onClick={onClick}
                  sx={{
                    cursor: demoMode ? 'default' : 'pointer',
                    borderBottom:
                      index === liabilities.length - 1
                        ? 'none'
                        : '1px solid #E0E0E0',
                    '&:hover': {
                      backgroundColor: demoMode ? 'inherit' : '#f9f9f9',
                    },
                  }}
                >
                  <TableCell sx={{ border: 'none', py: 1.5 }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      {logo ? (
                        <Avatar src={logo} sx={{ width: 32, height: 32 }}>
                          {name?.[0] ?? '?'}
                        </Avatar>
                      ) : (
                        <IconButton
                          aria-label="edit"
                          size="small"
                          sx={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: '#eee',
                          }}
                        >
                          <AccountIconSelector
                            fontSize="inherit"
                            subType={item?.subType}
                          />
                        </IconButton>
                      )}
                      <Typography>{name}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ border: 'none', py: 1.5, fontWeight: 500 }}
                  >
                    {dollarFormat.format(amount)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Button */}
      <Box mt={2}>
        <Button
          variant="contained"
          onClick={() => {
            if (!demoMode) {
              openModal('addAccounts', {
                getAccounts,
                openToPage: 'Liabilities',
              })
            }
          }}
          sx={{
            ml: 1.5,
            mb: 2,
            padding: '8px 24px',
            backgroundColor: '#262627',
            border: '2px solid #262627',
            color: '#fff',
            boxShadow: 'none',
            borderRadius: '100px',
            fontWeight: 600,
            fontSize: { xs: '14px', md: '18px' },
            textTransform: 'none',
            lineHeight: 1.5,
            '&:hover, &:focus, &:active, &:focus:not(:hover)': {
              transform: 'none',
              backgroundColor: '#262627fa',
              borderColor: '#262627fa',
              boxShadow: 'none',
            },
            '&:disabled': {
              borderColor: 'rgba(0,0,0,0)',
            },
          }}
        >
          + Add another Liability
        </Button>
      </Box>
    </Box>
  )
}

export default LiabilityReview
